/**
 * Small helpers around super-admin impersonation. The backend mints two custom
 * tokens at impersonation time: one for the target user (used to sign in) and
 * one for the super admin themselves (stashed here so a banner can offer a
 * one-click return without re-entering a password).
 *
 * SessionStorage is used on purpose — clears on tab close, scoped per tab,
 * never sent to a server.
 */

const RETURN_TOKEN_KEY = 'impersonation_return_token';
const ORIGIN_EMAIL_KEY = 'impersonation_origin_email';
const TARGET_AGENCY_KEY = 'impersonation_target_agency';
const TARGET_EMAIL_KEY = 'impersonation_target_email';

export interface ImpersonationState {
  returnToken: string;
  originEmail: string;
  targetAgency?: string;
  targetEmail?: string;
}

export function rememberImpersonationReturn(
  returnToken: string,
  originEmail: string,
  target?: { agencyName?: string; email?: string },
): void {
  try {
    sessionStorage.setItem(RETURN_TOKEN_KEY, returnToken);
    sessionStorage.setItem(ORIGIN_EMAIL_KEY, originEmail);
    if (target?.agencyName) sessionStorage.setItem(TARGET_AGENCY_KEY, target.agencyName);
    if (target?.email) sessionStorage.setItem(TARGET_EMAIL_KEY, target.email);
  } catch {
    // sessionStorage can throw in private browsing — non-fatal, just no banner
  }
}

export function getImpersonationState(): ImpersonationState | null {
  try {
    const returnToken = sessionStorage.getItem(RETURN_TOKEN_KEY);
    const originEmail = sessionStorage.getItem(ORIGIN_EMAIL_KEY);
    if (!returnToken || !originEmail) return null;
    const targetAgency = sessionStorage.getItem(TARGET_AGENCY_KEY) || undefined;
    const targetEmail = sessionStorage.getItem(TARGET_EMAIL_KEY) || undefined;
    return { returnToken, originEmail, targetAgency, targetEmail };
  } catch {
    return null;
  }
}

export function clearImpersonationReturn(): void {
  try {
    sessionStorage.removeItem(RETURN_TOKEN_KEY);
    sessionStorage.removeItem(ORIGIN_EMAIL_KEY);
    sessionStorage.removeItem(TARGET_AGENCY_KEY);
    sessionStorage.removeItem(TARGET_EMAIL_KEY);
  } catch {
    // ignore
  }
}
